import { useRef, forwardRef, RefObject } from "react";
import { useStyles } from "./Button.style";
import clsx from "classnames";
import { Icon, IconName } from "../Icon/Icon";
import { CircularProgress } from "../CircularProgress";

type ButtonVariant = "contained" | "outlined" | "borderless";
type ButtonSize = "smaller" | "small" | "large";

export interface IButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  color?: string;
  size?: ButtonSize;
  disabled?: boolean;
  disabledRipple?: boolean;
  fullWidth?: boolean;
  afterIcon?: string;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, IButtonProps>(
  (
    {
      className,
      variant = "contained",
      size = "large",
      disabled = false,
      fullWidth = false,
      color,
      disabledRipple,
      children,
      afterIcon,
      loading,
      ...otherProps
    },
    ref,
  ) => {
    const classes = useStyles();
    const currentButtonRef = useRef<HTMLButtonElement>(null);
    const mainButtonRef =
      (ref as RefObject<HTMLButtonElement>) || currentButtonRef;
    function createRipple(event: React.TouchEvent<HTMLButtonElement>) {
      const button = event.currentTarget;
      const circle = document.createElement("span");
      const diameter = Math.max(button.clientWidth, button.clientHeight);
      circle.style.width = circle.style.height = `${diameter}px`;
      circle.classList.add("ripple");

      const ripple = button.getElementsByClassName("ripple")[0];

      if (ripple) {
        ripple.remove();
      }

      button.appendChild(circle);
    }

    function onClickBtn(event: React.MouseEvent<HTMLButtonElement>) {
      if (disabled) return;
      otherProps?.onClick?.(event);
    }

    return (
      <button
        id="button"
        ref={mainButtonRef}
        {...otherProps}
        className={clsx(
          classes.root,
          variant,
          size,
          { disabled },
          { fullWidth },
          afterIcon && !children && "only-icon",
          className,
        )}
        style={{
          background: color,
        }}
        onTouchStart={(e: React.TouchEvent<HTMLButtonElement>) => {
          if (disabled || disabledRipple) return;
          createRipple(e);
        }}
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => onClickBtn(e)}
      >
        {color === "primary" && !disabled && (
          <div className={classes.overlay} />
        )}
        {loading ? (
          <CircularProgress enableAnimation size={20} borderLight />
        ) : (
          <>
            {children}
            {!!afterIcon && (
              <Icon
                name={afterIcon as IconName}
                className={clsx(
                  classes.iconSize,
                  size,
                  !children && "only-icon",
                )}
              />
            )}
          </>
        )}
      </button>
    );
  },
);

Button.displayName = "Button";

export default Button;
